import React from 'react';

import PropTypes from 'prop-types';

import {FieldShape} from 'commons/shapes';
import {InferPropTypes} from 'commons/types/Types';
import {
    EfeCatalogValue,
    EfeResponse,
    ScreenFilterValues,
    SepomexCatalog,
    SepomexCatalogResponse,
    ShareholderServiceRequest,
    View,
} from 'commons/models';
import {Form, FormikProps} from 'formik';
import {EfeButtonPrimary, EfeFormSection, EfeSupportMessage} from 'components';
import {Box, Grid} from '@material-ui/core';
import {CallMade} from '@material-ui/icons';
import {useSnackbar} from 'notistack';
import {useAlert, useSpinner} from 'hooks';
import {CatalogServices, LayoutServices} from 'services';
import {
    CONSTANT_VALUES,
    HttpCodes,
    Messages,
    Roles,
    SnackbarVariants,
    Status,
    ViewChildId,
} from 'commons/constants';
import EfeModalMsg from 'components/efe-modal-msg/EfeModalMsg';

InnerFormShareholderPersonalInfo.propTypes = {
    viewId: PropTypes.number.isRequired,
    fieldHandler: PropTypes.func.isRequired,
    fields: PropTypes.arrayOf(PropTypes.shape(FieldShape).isRequired).isRequired,
};

function InnerFormShareholderPersonalInfo(
    props: InferPropTypes<
        typeof InnerFormShareholderPersonalInfo.propTypes & FormikProps<ShareholderServiceRequest>
    >
) {
    const {
        values,
        errors,
        fields,
        isSubmitting,
        setFieldValue,
        fieldHandler,
        viewId,
        validateForm,
    } = props;
    const {enqueueSnackbar} = useSnackbar();
    const [zipCode, setZipCode] = React.useState<string>('');
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const {showAlert, defaultError, genericError, showAlertError, closeAlert} = useAlert();

    async function searchZipCode(value: string, colonyKey: number, stateKey: number) {
        if (value && value.length === 5 && zipCode !== value) {
            resetValues();
            setZipCode(value);
            setIsLoading(true);
            try {
                const sepomexData: EfeResponse<SepomexCatalogResponse> =
                    await CatalogServices.getSepomexCatalog(value);
                if (sepomexData.data && sepomexData.data.data && sepomexData.data.data.length > 0) {
                    setCatalogValues([...sepomexData.data.data], value, colonyKey, stateKey);
                } else {
                    setCatalogValues([], value, colonyKey, stateKey);
                    resetValues();
                    enqueueSnackbar(`${Messages.SEPOMEX_ERROR}${value}`, {
                        variant: SnackbarVariants.ERROR,
                    });
                }
                setIsLoading(false);
            } catch (error: any) {
                resetValues();
                console.error(error);
                setIsLoading(false);
                setCatalogValues([], value, colonyKey, stateKey);
                if (error && error.code < HttpCodes.INTERNAL_SERVER_ERROR) {
                    enqueueSnackbar(`${Messages.SEPOMEX_ERROR}${value}`, {
                        variant: SnackbarVariants.WARNING,
                    });
                } else {
                    enqueueSnackbar(`${Messages.SEPOMEX_ERROR}${value}`, {
                        variant: SnackbarVariants.ERROR,
                    });
                }
            }
        }
    }

    function setValues(data: SepomexCatalog, zipCodeAux: string) {
        if (zipCodeAux) {
            setFieldValue('zipCode', zipCodeAux);
        }
        if (data && data.municipalityKey) {
            setFieldValue('municipalityKey', data.municipalityKey);
        }
        if (data && data.municipality) {
            setFieldValue('municipalityDescription', data.municipality);
        }
        if (data && data.stateKey) {
            setFieldValue('stateKey', data.stateKey);
        }
        if (data && data.state) {
            setFieldValue('stateDescription', data.state);
        }
        setFieldValue('countryKey', CONSTANT_VALUES.MexicoId);
    }
    function setCatalogValues(
        data: Array<SepomexCatalog>,
        zipCode: string,
        colonyKey: number,
        stateKey: number
    ) {
        const colony: Array<EfeCatalogValue> = [];
        const state: Array<EfeCatalogValue> = [];
        let colonyItem: EfeCatalogValue;
        let stateItem: EfeCatalogValue;
        data.map((item: SepomexCatalog, index: number) => {
            if (!colonyItem || colonyItem.id !== item.settlementCpconsId) {
                colonyItem = {
                    id: item.settlementCpconsId,
                    order: index,
                    value: item.settlement,
                    status: Status.Active,
                };
                colony.push({...colonyItem});
            }
            if (!stateItem || stateItem.id !== item.stateKey) {
                stateItem = {
                    id: item.stateKey,
                    order: index,
                    value: item.state,
                    status: Status.Active,
                };
                state.push({...stateItem});
            }
        });
        if (fields && fields.length && values.addressOpt) {
            fields[colonyKey].catalogValues = [...colony];
            fields[stateKey].catalogValues = [...state];
            if (data && data.length > 0) {
                setValues(data[0], zipCode);
            }
        }
    }

    function resetValues() {
        setFieldValue('municipalityKey', '');
        setFieldValue('municipalityDescription', '');
        setFieldValue('stateKey', '');
        setFieldValue('stateDescription', '');
        setFieldValue('countryKey', '');
    }

    async function getFieldVariant() {
        try {
            setIsLoading(true);
            const request: ScreenFilterValues = {
                viewId: viewId,
                personType: values.personTypeKey,
                foreign: values.foreignCitizen,
                address: values.addressOpt,
                rol: Roles.SHAREHOLDER,
                viewKey: ViewChildId.SHAREHOLDER_V_DATA_INFORMATION_PHYSICAL,
            };
            const response: EfeResponse<View> = await LayoutServices.getViewByFilters(request);
            if (response.data && response.data.fields) {
                setIsLoading(false);
                await fieldHandler(values.personTypeKey, response.data);
            } else {
                enqueueSnackbar(Messages.LAYOUT_ERROR, {
                    variant: SnackbarVariants.ERROR,
                });
                fieldHandler(values.personTypeKey, undefined);
                setIsLoading(false);
            }
            validateForm();
        } catch (error) {
            setIsLoading(false);
            fieldHandler(values.personTypeKey, undefined);
            showAlertError(error, Messages.LAYOUT_ERROR);
        }
    }

    React.useEffect(() => {
        if (values.personTypeKey) {
            getFieldVariant();
        } else {
            fieldHandler(values.personTypeKey, undefined);
        }
    }, [values.foreignCitizen, values.personTypeKey, values.addressOpt]);

    React.useEffect(() => {
        const colonyKey = fields.findIndex((field) => field.attrName === 'colonyKey');
        const stateKey = fields.findIndex((field) => field.attrName === 'stateKey');
        if (
            values.zipCode &&
            values.zipCode.length === 5 &&
            !values.foreignCitizen &&
            colonyKey >= 0 &&
            stateKey >= 0
        ) {
            searchZipCode(values.zipCode, colonyKey, stateKey);
        }
    }, [values.zipCode, fields]);

    /* eslint-disable react/jsx-handler-names */
    return (
        <Form noValidate>
            {isLoading && useSpinner(isLoading)}
            {showAlert && (
                <EfeModalMsg
                    error={genericError}
                    open={showAlert}
                    defaultError={defaultError}
                    handleAction={closeAlert}
                />
            )}
            {fields && fields.length > 0 && (
                <Box m={2}>
                    <EfeSupportMessage />
                    <EfeFormSection
                        fields={fields}
                        values={values}
                        errors={errors}
                        setFieldValue={setFieldValue}
                    />
                    <Grid container direction='row' justifyContent='flex-end'>
                        <Box pt={6} width={160}>
                            <EfeButtonPrimary
                                fullWidth
                                disabled={isSubmitting}
                                type='submit'
                                endIcon={<CallMade />}>
                                {'OK'}
                            </EfeButtonPrimary>
                        </Box>
                    </Grid>
                </Box>
            )}
        </Form>
    );
}

export default InnerFormShareholderPersonalInfo;
